// tour-link-editor.ts — per-instance controller for the two-pane virtual-tour
// link editor: mounts TWO independent PSV placement viewers side-by-side (one
// per side of a hotspot link) so the operator can click a spot in each pano
// simultaneously, instead of tour-place.ts's mutually-exclusive one-viewer-
// at-a-time pane.
//
// Loads the SAME standalone pano-editor bundle as tour-place.ts and
// pano-crop.ts (static/js/pano-editor/, built by `make build-pano-editor`),
// with the identical fixed-path, single-flight <script> injection — that
// part of tour-place.ts's design is shared/idempotent infrastructure and
// stays as-is. What does NOT carry over is tour-place.ts's module-level
// `currentHandle`/`placedPose`/`placedCb`/`openToken` singleton: this editor
// shows both panes live at once, so each `mountLinkPane` call must own an
// independent handle/pose/callback closure with no teardown-first guard
// against a second concurrent mount.
const PANO_EDITOR_SRC = "/static/js/pano-editor/dist/pano-editor.js";

interface PanoEditorPose {
  yaw: number;
  pitch: number;
}

// Field-for-field identical to tour-place.ts's and pano-crop.ts's own copy
// of this shape — required for the ambient Window.StudioPanoEditor
// declaration below to merge cleanly with theirs (TS errors TS2717/TS2741 on
// any mismatch across files augmenting the same global interface member).
interface PanoEditorMarker {
  id: string;
  yaw: number;
  pitch: number;
  label?: string;
}

export type PanoMarker = PanoEditorMarker;

interface PanoEditorHandle {
  getPose(): PanoEditorPose;
  resize(): void;
  destroy(): void;
  setMarkers(markers: PanoEditorMarker[]): void;
}

declare global {
  interface Window {
    StudioPanoEditor?: {
      mount(
        container: HTMLElement,
        equirectUrl: string,
        opts?: {
          yaw?: number;
          pitch?: number;
          placementMode?: boolean;
          markers?: PanoEditorMarker[];
          onPlace?(pose: PanoEditorPose): void;
        },
      ): PanoEditorHandle;
    };
  }
}

// Single-flight script load, guarded by a module-level Promise — identical
// pattern to tour-place.ts's/pano-crop.ts's loadPanoEditor(). Both link-pane
// mounts (left side + right side of a hotspot) fire during the same editor
// open, so this MUST stay shared: two concurrent mountLinkPane() calls must
// inject the <script> tag only once. Cleared on failure so a later retry
// (e.g. a transient network blip) gets a fresh attempt instead of replaying
// a rejected promise forever.
let loadPromise: Promise<void> | null = null;

function loadPanoEditor(): Promise<void> {
  if (window.StudioPanoEditor) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    const el = document.createElement("script");
    el.src = PANO_EDITOR_SRC;
    el.onload = () => resolve();
    el.onerror = () => {
      loadPromise = null;
      reject(new Error(`failed to load ${PANO_EDITOR_SRC}`));
    };
    document.head.appendChild(el);
  });
  return loadPromise;
}

export interface LinkPane {
  setMarkers(m: PanoMarker[]): void;
  getPose(): { yaw: number; pitch: number } | null;
  resize(): void;
  destroy(): void;
}

export interface MountLinkPaneOptions {
  yaw?: number;
  pitch?: number;
  markers?: PanoMarker[];
  onPlace?(pose: { yaw: number; pitch: number }): void;
}

// mountLinkPane loads the (shared, single-flight) viewer bundle and mounts a
// FRESH, independent placement viewer into `host`, in placementMode (click ->
// yaw/pitch pick, same as tour-place.ts). Unlike openTourPlacer, this does
// NOT tear down any prior pane first — the two-pane editor keeps both sides
// mounted simultaneously, so a second call must never destroy the first
// pane's viewer.
//
// The bundle load is async, but this function itself is not: it returns the
// LinkPane handle immediately, and the actual window.StudioPanoEditor.mount()
// call happens once the (possibly already-satisfied, possibly still
// in-flight) script load resolves. Every LinkPane method is a safe no-op
// until then — mirrors the "operator can't usefully interact before the
// WebGL viewer exists" reality of the async load, without forcing every
// caller to await a promise just to get a pane it can pass around.
export function mountLinkPane(
  host: HTMLElement,
  equirectUrl: string,
  opts: MountLinkPaneOptions,
): LinkPane {
  let handle: PanoEditorHandle | null = null;
  let placedPose: PanoEditorPose | null = null;
  let destroyed = false;

  loadPanoEditor()
    .then(() => {
      // Destroyed while the script/mount was in flight — don't mount an
      // orphaned WebGL viewer into a host the operator already dismissed.
      if (destroyed) return;
      if (!window.StudioPanoEditor) {
        throw new Error("StudioPanoEditor missing after script load");
      }
      handle = window.StudioPanoEditor.mount(host, equirectUrl, {
        yaw: opts.yaw,
        pitch: opts.pitch,
        placementMode: true,
        markers: opts.markers,
        onPlace: (placed) => {
          placedPose = placed;
          opts.onPlace?.(placed);
        },
      });
    })
    .catch((err) => {
      // eslint-disable-next-line no-console -- surfaced the same way
      // tour-place.ts/pano-crop.ts leave load failures for their callers to
      // notice via the console; there's no UI affordance to route this to
      // from inside a fire-and-forget mount.
      console.error("mountLinkPane: failed to load pano editor", err);
    });

  return {
    setMarkers(m: PanoMarker[]): void {
      handle?.setMarkers(m);
    },
    getPose(): { yaw: number; pitch: number } | null {
      return placedPose;
    },
    resize(): void {
      handle?.resize();
    },
    destroy(): void {
      destroyed = true;
      handle?.destroy();
      handle = null;
    },
  };
}
